/**
 * Frontend mirror of packages/api/src/prompts/firmNameUtils.ts.
 * Used for live previews in the agent edit dialog so users can see what
 * voice agents will actually say BEFORE they save.
 *
 * Keep in sync with the backend version. If logic diverges, the backend
 * is authoritative — the live preview here is best-effort only.
 */

const SUFFIX_PATTERNS = [
  /\bLLP\b/gi,
  /\bLLC\b/gi,
  /\bPC\b/gi,
  /\bPLLC\b/gi,
  /\bP\.?A\.?\b/gi,
  /\bP\.?C\.?\b/gi,
  /\bP\.?L\.?L\.?C\.?\b/gi,
  /\bL\.?L\.?P\.?\b/gi,
  /\bInc\.?\b/gi,
  /\bCorp\.?\b/gi,
  /,?\s*Attorneys?\s+at\s+Law\b/gi,
  /,?\s*&\s*Associates?\b/gi,
  /,?\s*and\s+Associates?\b/gi,
  /,?\s*Law\s+(Firm|Office|Offices|Group|Practice)\b/gi,
]

export function generateFirmShortName(fullName: string): string {
  if (!fullName || !fullName.trim()) return 'Our Firm'
  let name = fullName.trim()
  name = name.replace(/^The\s+Law\s+(Offices?|Firm)\s+of\s+/i, '')
  name = name.replace(/^Law\s+(Offices?|Firm)\s+of\s+/i, '')
  for (const pattern of SUFFIX_PATTERNS) name = name.replace(pattern, '')
  name = name.replace(/\s*[,&]\s*/g, ' ')
  name = name.replace(/\s+/g, ' ').trim()
  name = name.replace(/[.,;:]+$/, '').trim()
  if (!name) return 'Our Firm'
  const words = name.split(/\s+/)
  if (words.length > 3) return words.slice(0, 3).join(' ')
  return name
}

/**
 * Resolve what voice agents will actually say. Prefers a non-blank
 * speakable override, else falls back to the auto-shortener.
 */
export function resolveFirmShortName(
  override: string | null | undefined,
  fullName: string
): string {
  const trimmed = (override || '').trim()
  if (trimmed) return trimmed
  return generateFirmShortName(fullName)
}

/**
 * Build the default greeting line for live preview, mirroring the backend's
 * buildFirstMessage() (packages/api/src/prompts/firstMessage.ts).
 *
 * Uses {{timeOfDay}} as a runtime variable; firmName and agentName are
 * resolved at preview-render time using the dialog's current input values.
 */
export function buildLiveGreeting(opts: {
  agentName: string
  firmName: string
  assistantType?: 'inbound' | 'outbound'
  vertical?: 'saas' | 'law_firm'
}): string {
  const { agentName, firmName, assistantType = 'inbound', vertical = 'law_firm' } = opts
  if (assistantType === 'outbound') {
    return 'Hey, is this {{firstName}}?'
  }
  const persona = vertical === 'saas' ? 'your AI assistant' : "the firm's AI assistant"
  const firm = firmName || 'your firm'
  if (agentName) {
    return `Good {{timeOfDay}}, thank you for calling ${firm}. This is ${agentName}, ${persona}. How can I help you today?`
  }
  return `Good {{timeOfDay}}, thank you for calling ${firm}. How can I help you today?`
}
